import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { RutField, fillEmpty, lookupValues } from '../components/RutField';
import { toast } from 'sonner';
import CompanyTypeWizard from '../components/CompanyTypeWizard';
import {
  FileText,
  ArrowLeft,
  Scale,
  Gavel,
  Briefcase,
  Building2,
  Users,
  Calculator,
  Shield,
  ChevronRight,
  ChevronDown,
  Loader2,
  Download,
  Copy,
  Check,
  AlertTriangle,
  Info,
  Clock,
  Star,
  Sparkles,
  CheckCircle,
  XCircle,
  DollarSign,
  Calendar,
  User,
  FileDown,
  Percent,
  HelpCircle
} from 'lucide-react';

const CATEGORY_ICONS = {
  civil: Scale,
  laboral: Briefcase,
  constitucional: Shield,
  tributario: Calculator,
  administrativo: Building2,
  familia: Users,
  empresas: Building2,
  otros: FileText
};

const CATEGORY_COLORS = {
  civil: 'blue',
  laboral: 'green',
  constitucional: 'purple',
  tributario: 'amber',
  administrativo: 'cyan',
  familia: 'pink',
  empresas: 'emerald',
  otros: 'slate'
};

// RUT Validation Utilities
const cleanRut = (rut) => {
  if (!rut) return '';
  return rut.replace(/[.-]/g, '').toUpperCase();
};

const formatRut = (rut) => {
  if (!rut) return '';
  let cleaned = cleanRut(rut).replace(/[^0-9K]/gi, '');
  if (cleaned.length < 2) return cleaned;
  
  const body = cleaned.slice(0, -1);
  const dv = cleaned.slice(-1);
  
  let formatted = '';
  for (let i = body.length - 1, count = 0; i >= 0; i--, count++) {
    if (count > 0 && count % 3 === 0) formatted = '.' + formatted;
    formatted = body[i] + formatted;
  }
  return `${formatted}-${dv}`;
};

const calculateVerificationDigit = (rutBody) => {
  const body = rutBody.replace(/\D/g, '');
  if (!body) return '';
  
  let sum = 0;
  let multiplier = 2;
  for (let i = body.length - 1; i >= 0; i--) {
    sum += parseInt(body[i]) * multiplier;
    multiplier = multiplier === 7 ? 2 : multiplier + 1;
  }
  
  const remainder = sum % 11;
  const digit = 11 - remainder;
  if (digit === 11) return '0';
  if (digit === 10) return 'K';
  return digit.toString();
};

const validateRutFull = (rut) => {
  if (!rut) return { isValid: false, error: 'RUT requerido' };
  
  const cleaned = cleanRut(rut);
  if (cleaned.length < 8) return { isValid: false, error: 'RUT muy corto' };
  if (cleaned.length > 12) return { isValid: false, error: 'RUT muy largo' };
  
  const body = cleaned.slice(0, -1);
  const providedDv = cleaned.slice(-1);
  
  if (!/^\d+$/.test(body)) return { isValid: false, error: 'RUT contiene caracteres inválidos' };
  if (!/^[0-9K]$/.test(providedDv)) return { isValid: false, error: 'Dígito verificador inválido' };
  
  const expectedDv = calculateVerificationDigit(body);
  if (providedDv !== expectedDv) {
    return { isValid: false, error: `Dígito incorrecto (esperado: ${expectedDv})` };
  }
  return { isValid: true };
};

const getRutType = (rut) => {
  const cleaned = cleanRut(rut);
  const body = cleaned.slice(0, -1);
  const number = parseInt(body);
  if (isNaN(number)) return null;
  if (number < 50000000) return 'persona';
  if (number >= 50000000 && number < 60000000) return 'extranjero';
  return 'empresa';
};

// Enhanced RUT Input Component with real-time validation
const RutInput = ({ value, onChange, label, required }) => {
  const [displayValue, setDisplayValue] = useState('');
  const [validation, setValidation] = useState({ isValid: false, error: null });
  const [touched, setTouched] = useState(false);
  const [rutType, setRutType] = useState(null);

  useEffect(() => {
    if (value) setDisplayValue(formatRut(value));
  }, []);

  const handleChange = (e) => {
    let inputValue = e.target.value.replace(/[^0-9kK.-]/g, '');
    const formatted = formatRut(inputValue);
    setDisplayValue(formatted);
    
    const cleanValue = cleanRut(formatted);
    if (onChange) onChange(cleanValue);
    
    // Validate
    if (!cleanValue) {
      setValidation({ isValid: false, error: required ? 'RUT requerido' : null });
      setRutType(null);
      return;
    }
    
    const result = validateRutFull(cleanValue);
    setValidation(result);
    setRutType(result.isValid ? getRutType(cleanValue) : null);
  };

  const showError = touched && validation.error && !validation.isValid;
  const showSuccess = touched && validation.isValid;

  return (
    <div className="space-y-1">
      <label className="text-sm text-slate-400 flex items-center gap-2">
        <User className="w-4 h-4" />
        {label} {required && <span className="text-red-400">*</span>}
      </label>
      <div className="relative">
        <input
          type="text"
          value={displayValue}
          onChange={handleChange}
          onBlur={() => setTouched(true)}
          placeholder="12.345.678-9"
          maxLength={12}
          className={`w-full px-4 py-2.5 bg-slate-800 border rounded-lg text-white focus:outline-none pr-28 transition-colors ${
            !touched ? 'border-slate-700 focus:border-purple-500' :
            showSuccess ? 'border-emerald-500/50 focus:border-emerald-500' :
            showError ? 'border-red-500/50 focus:border-red-500' :
            'border-slate-700 focus:border-purple-500'
          }`}
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
          {/* RUT Type Badge */}
          {rutType && validation.isValid && (
            <span className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
              rutType === 'persona' ? 'bg-blue-500/20 text-blue-400' :
              rutType === 'empresa' ? 'bg-purple-500/20 text-purple-400' :
              'bg-amber-500/20 text-amber-400'
            }`}>
              {rutType === 'persona' ? (
                <><User className="w-3 h-3" /> Persona</>
              ) : rutType === 'empresa' ? (
                <><Building2 className="w-3 h-3" /> Empresa</>
              ) : (
                <><AlertTriangle className="w-3 h-3" /> Extranjero</>
              )}
            </span>
          )}
          {/* Validation Icon */}
          {touched && displayValue && (
            <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
              showSuccess ? 'bg-emerald-500/20' : showError ? 'bg-red-500/20' : ''
            }`}>
              {showSuccess && <CheckCircle className="w-4 h-4 text-emerald-400" />}
              {showError && <XCircle className="w-4 h-4 text-red-400" />}
            </div>
          )}
        </div>
      </div>
      {showError && (
        <p className="text-xs text-red-400 flex items-center gap-1">
          <AlertTriangle className="w-3 h-3" /> {validation.error}
        </p>
      )}
      {showSuccess && (
        <p className="text-xs text-emerald-400 flex items-center gap-1">
          <CheckCircle className="w-3 h-3" /> RUT válido
        </p>
      )}
    </div>
  );
};

// Money Input with formatting
const MoneyInput = ({ value, onChange, label, required }) => {
  const [displayValue, setDisplayValue] = useState('');

  useEffect(() => {
    if (value) {
      const num = parseInt(value.toString().replace(/\D/g, ''));
      if (!isNaN(num)) {
        setDisplayValue(num.toLocaleString('es-CL'));
      }
    } else {
      setDisplayValue('');
    }
  }, [value]);

  const handleChange = (e) => {
    const raw = e.target.value.replace(/\D/g, '');
    onChange(raw);
  };

  return (
    <div className="space-y-1">
      <label className="text-sm text-slate-400 flex items-center gap-2">
        <DollarSign className="w-4 h-4" />
        {label} {required && <span className="text-red-400">*</span>}
      </label>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
        <input
          type="text"
          value={displayValue}
          onChange={handleChange}
          placeholder="1.500.000"
          className="w-full pl-8 pr-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-purple-500 focus:outline-none"
        />
      </div>
    </div>
  );
};

// Severance Calculator Component
const SeveranceCalculator = ({ onCalculated, initialData }) => {
  const { api } = useAuth();
  const [calculating, setCalculating] = useState(false);
  const [result, setResult] = useState(null);
  const [formData, setFormData] = useState({
    monthly_salary: initialData?.sueldo || '',
    start_date: initialData?.fecha_inicio || '',
    end_date: initialData?.fecha_despido || new Date().toISOString().split('T')[0],
    surcharge_percent: 30
  });

  const handleCalculate = async () => {
    if (!formData.monthly_salary || !formData.start_date || !formData.end_date) {
      toast.error('Complete todos los campos');
      return;
    }

    setCalculating(true);
    try {
      const response = await api.post('/legal-docs-v2/calculate/severance', {
        monthly_salary: parseInt(formData.monthly_salary),
        start_date: formData.start_date,
        end_date: formData.end_date,
        surcharge_percent: formData.surcharge_percent
      });
      
      setResult(response.data);
      onCalculated && onCalculated(response.data);
      toast.success('Cálculo realizado');
    } catch (error) {
      toast.error('Error al calcular');
    } finally {
      setCalculating(false);
    }
  };

  return (
    <div className="bg-gradient-to-br from-green-900/30 to-emerald-900/30 border border-green-500/30 rounded-xl p-4 space-y-4">
      <div className="flex items-center gap-2 text-green-400 font-semibold">
        <Calculator className="w-5 h-5" />
        Calculadora de Indemnización
      </div>

      <div className="grid grid-cols-2 gap-3">
        <MoneyInput
          label="Sueldo mensual"
          value={formData.monthly_salary}
          onChange={(v) => setFormData(prev => ({ ...prev, monthly_salary: v }))}
        />
        
        <div className="space-y-1">
          <label className="text-sm text-slate-400 flex items-center gap-2">
            <Percent className="w-4 h-4" />
            Recargo legal
          </label>
          <select
            value={formData.surcharge_percent}
            onChange={(e) => setFormData(prev => ({ ...prev, surcharge_percent: parseInt(e.target.value) }))}
            className="w-full px-3 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-green-500 focus:outline-none"
          >
            <option value={0}>Sin recargo</option>
            <option value={30}>30% - Art. 161 (Necesidades empresa)</option>
            <option value={50}>50% - Art. 160 (Causal no probada)</option>
            <option value={80}>80% - Discriminación</option>
          </select>
        </div>

        <div className="space-y-1">
          <label className="text-sm text-slate-400 flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            Fecha inicio
          </label>
          <input
            type="date"
            value={formData.start_date}
            onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
            className="w-full px-3 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-green-500 focus:outline-none"
          />
        </div>

        <div className="space-y-1">
          <label className="text-sm text-slate-400 flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            Fecha despido
          </label>
          <input
            type="date"
            value={formData.end_date}
            onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
            className="w-full px-3 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-green-500 focus:outline-none"
          />
        </div>
      </div>

      <button
        onClick={handleCalculate}
        disabled={calculating}
        className="w-full py-2.5 bg-green-600 hover:bg-green-700 disabled:opacity-50 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
      >
        {calculating ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Calculator className="w-4 h-4" />
        )}
        Calcular Indemnización
      </button>

      {result && (
        <div className="mt-4 space-y-3 bg-slate-900/50 rounded-lg p-4">
          <h4 className="font-semibold text-white text-sm">Resultado del Cálculo</h4>
          
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-400">Años de servicio:</span>
              <span className="text-white font-medium">{result.severance_details.years_of_service} años</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">Indemnización años servicio:</span>
              <span className="text-white">{result.breakdown.indemnizacion_anos_servicio}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">Aviso previo:</span>
              <span className="text-white">{result.breakdown.indemnizacion_aviso_previo}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">Feriado proporcional:</span>
              <span className="text-white">{result.breakdown.feriado_proporcional}</span>
            </div>
            {result.surcharge_details.surcharge_percent > 0 && (
              <div className="flex justify-between text-amber-400">
                <span>Recargo {result.surcharge_details.surcharge_percent}%:</span>
                <span>{Object.entries(result.breakdown).find(([k]) => k.includes('recargo'))?.[1]}</span>
              </div>
            )}
            <div className="flex justify-between pt-2 border-t border-slate-700">
              <span className="text-green-400 font-semibold">TOTAL:</span>
              <span className="text-green-400 font-bold text-lg">{result.formatted_total}</span>
            </div>
          </div>

          <p className="text-xs text-slate-500 mt-2">
            Base legal: {result.legal_basis}
          </p>
        </div>
      )}
    </div>
  );
};

// Alimony Calculator Component
const AlimonyCalculator = ({ onCalculated }) => {
  const { api } = useAuth();
  const [calculating, setCalculating] = useState(false);
  const [result, setResult] = useState(null);
  const [formData, setFormData] = useState({
    debtor_income: '',
    number_of_children: 1,
    special_needs: false
  });

  const handleCalculate = async () => {
    if (!formData.debtor_income) {
      toast.error('Ingrese el ingreso del alimentante');
      return;
    }

    setCalculating(true);
    try {
      const response = await api.post('/legal-docs-v2/calculate/alimony', {
        debtor_income: parseInt(formData.debtor_income),
        number_of_children: formData.number_of_children,
        special_needs: formData.special_needs
      });
      
      setResult(response.data);
      onCalculated && onCalculated(response.data);
      toast.success('Cálculo realizado');
    } catch (error) {
      toast.error('Error al calcular');
    } finally {
      setCalculating(false);
    }
  };

  return (
    <div className="bg-gradient-to-br from-pink-900/30 to-rose-900/30 border border-pink-500/30 rounded-xl p-4 space-y-4">
      <div className="flex items-center gap-2 text-pink-400 font-semibold">
        <Users className="w-5 h-5" />
        Calculadora de Pensión de Alimentos
      </div>

      <div className="space-y-3">
        <MoneyInput
          label="Ingreso mensual del alimentante"
          value={formData.debtor_income}
          onChange={(v) => setFormData(prev => ({ ...prev, debtor_income: v }))}
        />

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <label className="text-sm text-slate-400">Número de hijos</label>
            <select
              value={formData.number_of_children}
              onChange={(e) => setFormData(prev => ({ ...prev, number_of_children: parseInt(e.target.value) }))}
              className="w-full px-3 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-pink-500 focus:outline-none"
            >
              {[1, 2, 3, 4, 5].map(n => (
                <option key={n} value={n}>{n} {n === 1 ? 'hijo' : 'hijos'}</option>
              ))}
            </select>
          </div>

          <div className="space-y-1">
            <label className="text-sm text-slate-400">Necesidades especiales</label>
            <div className="flex items-center gap-2 h-[42px]">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.special_needs}
                  onChange={(e) => setFormData(prev => ({ ...prev, special_needs: e.target.checked }))}
                  className="w-4 h-4 rounded border-slate-600 text-pink-500 focus:ring-pink-500"
                />
                <span className="text-sm text-white">Sí, tiene necesidades especiales</span>
              </label>
            </div>
          </div>
        </div>
      </div>

      <button
        onClick={handleCalculate}
        disabled={calculating}
        className="w-full py-2.5 bg-pink-600 hover:bg-pink-700 disabled:opacity-50 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
      >
        {calculating ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Calculator className="w-4 h-4" />
        )}
        Calcular Pensión Sugerida
      </button>

      {result && (
        <div className="mt-4 space-y-3 bg-slate-900/50 rounded-lg p-4">
          <h4 className="font-semibold text-white text-sm">Pensión Sugerida</h4>
          
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-400">Porcentaje sugerido:</span>
              <span className="text-white font-medium">{(result.calculation.suggested_percent * 100).toFixed(0)}%</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">Monto sugerido:</span>
              <span className="text-pink-400 font-bold">{result.formatted.suggested_amount}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">Por cada hijo:</span>
              <span className="text-white">{result.formatted.per_child}</span>
            </div>
            <div className="flex justify-between text-slate-500">
              <span>Mínimo legal:</span>
              <span>{result.formatted.minimum_amount}</span>
            </div>
          </div>

          <p className="text-xs text-slate-500 mt-2">
            {result.calculation.legal_note}
          </p>
          <p className="text-xs text-slate-500">
            Base legal: {result.legal_basis}
          </p>
        </div>
      )}
    </div>
  );
};

// Main Page Component
const LegalDocumentsPageEnhanced = () => {
  const { api } = useAuth();
  
  // State
  const [activeTab, setActiveTab] = useState('browse');
  const [categories, setCategories] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [documentsByCategory, setDocumentsByCategory] = useState({});
  const [popularDocuments, setPopularDocuments] = useState([]);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [templateInfo, setTemplateInfo] = useState(null);
  const [formData, setFormData] = useState({});
  const [generatedDocument, setGeneratedDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [copied, setCopied] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [showCalculator, setShowCalculator] = useState(null); // 'severance' | 'alimony' | null
  const [calculationResult, setCalculationResult] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [showCompanyWizard, setShowCompanyWizard] = useState(false);
  
  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, []);
  
  const loadInitialData = async () => {
    try {
      const [categoriesRes, documentsRes, popularRes] = await Promise.all([
        api.get('/legal-docs/categories'),
        api.get('/legal-docs/types'),
        api.get('/legal-docs/popular')
      ]);
      
      setCategories(categoriesRes.data.categories || []);
      setDocuments(documentsRes.data.documents || []);
      setDocumentsByCategory(documentsRes.data.by_category || {});
      setPopularDocuments(popularRes.data.popular_documents || []);
      
      if (documentsRes.data.by_category) {
        const firstCat = Object.keys(documentsRes.data.by_category)[0];
        if (firstCat) {
          setExpandedCategories({ [firstCat]: true });
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Error al cargar documentos');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSelectDocument = async (docType) => {
    setSelectedDocument(docType);
    setGeneratedDocument(null);
    setFormData({});
    setCalculationResult(null);
    setShowCalculator(null);
    setValidationErrors({});
    
    // Determine if calculator should be shown
    const laborDocs = ['demanda_laboral', 'demanda_despido', 'demanda_tutela'];
    const familyDocs = ['demanda_alimentos'];
    
    if (laborDocs.includes(docType)) {
      setShowCalculator('severance');
    } else if (familyDocs.includes(docType)) {
      setShowCalculator('alimony');
    }
    
    try {
      const response = await api.get(`/legal-docs/template/${docType}`);
      setTemplateInfo(response.data);
      setActiveTab('generate');
    } catch (error) {
      console.error('Error loading template:', error);
      toast.error('Error al cargar plantilla');
    }
  };
  
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear validation error when field is modified
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const copy = { ...prev };
        delete copy[field];
        return copy;
      });
    }
  };

  const handleCalculationComplete = (result) => {
    setCalculationResult(result);
    
    // Auto-fill form fields from calculation
    if (result.severance_details) {
      setFormData(prev => ({
        ...prev,
        anos_servicio: `${result.severance_details.complete_years} años`,
        indemnizacion: result.formatted_total,
        total_prestaciones: result.formatted_total
      }));
    }
    if (result.calculation?.suggested_amount) {
      setFormData(prev => ({
        ...prev,
        monto_alimentos: result.formatted.suggested_amount
      }));
    }
  };
  
  const handleGenerate = async () => {
    const missingFields = templateInfo?.required_fields?.filter(field => !formData[field]);
    if (missingFields?.length > 0) {
      toast.error(`Campos requeridos: ${missingFields.slice(0, 3).join(', ')}...`);
      return;
    }
    
    setGenerating(true);
    try {
      const response = await api.post('/legal-docs/generate', {
        document_type: selectedDocument,
        data: formData
      });
      setGeneratedDocument(response.data);
      toast.success('Documento generado exitosamente');
    } catch (error) {
      console.error('Error generating:', error);
      toast.error(error.response?.data?.detail || 'Error al generar documento');
    } finally {
      setGenerating(false);
    }
  };

  const handleGeneratePdf = async () => {
    setGeneratingPdf(true);
    try {
      const response = await api.post('/legal-docs-v2/generate-pdf', {
        document_type: selectedDocument,
        data: formData,
        validate_fields: false
      });
      
      if (response.data.pdf_base64) {
        // Convert base64 to blob and download
        const byteCharacters = atob(response.data.pdf_base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = response.data.filename || `${selectedDocument}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        toast.success('PDF descargado');
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error(error.response?.data?.detail?.message || 'Error al generar PDF');
    } finally {
      setGeneratingPdf(false);
    }
  };
  
  const handleCopy = () => {
    if (generatedDocument?.content) {
      navigator.clipboard.writeText(generatedDocument.content);
      setCopied(true);
      toast.success('Documento copiado al portapapeles');
      setTimeout(() => setCopied(false), 2000);
    }
  };
  
  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  // Determine which input component to use based on field name
  const renderInputField = (field, index) => {
    const isRut = field.toLowerCase().includes('rut');
    const isMoney = field.toLowerCase().includes('sueldo') || 
                    field.toLowerCase().includes('monto') || 
                    field.toLowerCase().includes('indemnizacion') ||
                    field.toLowerCase().includes('total');
    const isDate = field.toLowerCase().includes('fecha');
    const isTextarea = field.toLowerCase().includes('hechos') || 
                       field.toLowerCase().includes('fundamentos') ||
                       field.toLowerCase().includes('peticiones') ||
                       field.toLowerCase().includes('descripcion');
    
    const label = field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

    if (isRut) {
      return (
        <div key={field} className="space-y-1">
          <label className="text-sm font-medium text-slate-300">{label} <span className="text-red-400">*</span></label>
          <RutField
            testid={`doc-field-${field}`}
            value={formData[field] || ''}
            onChange={(v) => handleInputChange(field, v)}
            onLookup={(d) => setFormData((prev) => fillEmpty(prev, lookupValues(field, templateInfo?.required_fields || [], d)))}
            className="w-full px-4 py-2.5 rounded-lg border border-slate-700 bg-slate-800/50 text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
          />
        </div>
      );
    }

    if (isMoney) {
      return (
        <MoneyInput
          key={field}
          label={label}
          value={formData[field] || ''}
          onChange={(v) => handleInputChange(field, v)}
          required={true}
        />
      );
    }

    if (isDate) {
      return (
        <div key={field} className="space-y-1">
          <label className="text-sm text-slate-400 flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            {label} <span className="text-red-400">*</span>
          </label>
          <input
            type="date"
            value={formData[field] || ''}
            onChange={(e) => handleInputChange(field, e.target.value)}
            className="w-full px-3 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-purple-500 focus:outline-none"
          />
        </div>
      );
    }

    if (isTextarea) {
      return (
        <div key={field} className="col-span-2 space-y-1">
          <label className="text-sm text-slate-400 flex items-center gap-2">
            <FileText className="w-4 h-4" />
            {label} <span className="text-red-400">*</span>
          </label>
          <textarea
            value={formData[field] || ''}
            onChange={(e) => handleInputChange(field, e.target.value)}
            placeholder={`Ingrese ${label.toLowerCase()}...`}
            rows={4}
            className="w-full px-3 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-purple-500 focus:outline-none resize-y"
          />
        </div>
      );
    }

    // Default text input
    return (
      <div key={field} className="space-y-1">
        <label className="text-sm text-slate-400">
          {label} <span className="text-red-400">*</span>
        </label>
        <input
          type="text"
          value={formData[field] || ''}
          onChange={(e) => handleInputChange(field, e.target.value)}
          placeholder={`Ingrese ${label.toLowerCase()}`}
          className="w-full px-3 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-purple-500 focus:outline-none"
        />
      </div>
    );
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background text-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-background text-white">
      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Link to="/dashboard" className="p-2 hover:bg-slate-800 rounded-lg transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-3">
              <Gavel className="w-7 h-7 text-purple-400" />
              Generador de Escritos Judiciales
              <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded-full font-medium">
                v2 Mejorado
              </span>
            </h1>
            <p className="text-slate-400 text-sm">
              Plantillas profesionales con validaciones y calculadoras integradas
            </p>
          </div>
        </div>
        
        {/* Tab Navigation */}
        <div className="flex gap-2 mb-6 border-b border-slate-700 pb-2">
          <button
            onClick={() => setActiveTab('browse')}
            className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors flex items-center gap-2 ${
              activeTab === 'browse'
                ? 'bg-purple-500/20 text-purple-400 border-b-2 border-purple-500'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            <FileText className="w-4 h-4" />
            Plantillas
          </button>
          <button
            onClick={() => setActiveTab('generate')}
            disabled={!selectedDocument}
            className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors flex items-center gap-2 disabled:opacity-50 ${
              activeTab === 'generate'
                ? 'bg-blue-500/20 text-blue-400 border-b-2 border-blue-500'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            <Sparkles className="w-4 h-4" />
            Generar Documento
            {selectedDocument && (
              <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded-full text-xs">
                {selectedDocument.replace(/_/g, ' ')}
              </span>
            )}
          </button>
        </div>
        
        {/* Browse Tab */}
        {activeTab === 'browse' && (
          <div className="grid md:grid-cols-3 gap-6">
            {/* Document List */}
            <div className="md:col-span-2 space-y-4">
              {/* Company Type Wizard Button */}
              <div className="bg-gradient-to-r from-emerald-900/30 to-teal-900/30 border border-emerald-500/30 rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                      <HelpCircle className="w-5 h-5 text-emerald-400" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-white text-sm">¿No sabes qué tipo de empresa crear?</h4>
                      <p className="text-xs text-slate-400">Te ayudamos a elegir en 4 preguntas</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowCompanyWizard(true)}
                    className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
                  >
                    <Sparkles className="w-4 h-4" />
                    Asesor IA
                  </button>
                </div>
              </div>

              <div className="bg-card border border-border rounded-xl p-5">
                <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-purple-400" />
                  Todas las Plantillas
                  <span className="text-xs text-slate-500">({documents.length} disponibles)</span>
                </h3>
                
                <div className="space-y-3">
                  {Object.entries(documentsByCategory).map(([category, categoryData]) => {
                    const Icon = CATEGORY_ICONS[category] || FileText;
                    const color = CATEGORY_COLORS[category] || 'slate';
                    const isExpanded = expandedCategories[category];
                    // Handle both array format and object format {name, documents}
                    const docs = Array.isArray(categoryData) ? categoryData : (categoryData?.documents || []);
                    const categoryName = categoryData?.name || category;
                    
                    return (
                      <div key={category} className="border border-slate-700 rounded-lg overflow-hidden">
                        <button
                          onClick={() => toggleCategory(category)}
                          className={`w-full px-4 py-3 flex items-center justify-between hover:bg-slate-800/50 transition-colors bg-${color}-500/10`}
                        >
                          <div className="flex items-center gap-3">
                            <Icon className={`w-5 h-5 text-${color}-400`} />
                            <span className="font-medium text-white">{categoryName}</span>
                            <span className={`px-2 py-0.5 bg-${color}-500/20 text-${color}-400 rounded-full text-xs`}>
                              {docs.length}
                            </span>
                          </div>
                          {isExpanded ? (
                            <ChevronDown className="w-5 h-5 text-slate-400" />
                          ) : (
                            <ChevronRight className="w-5 h-5 text-slate-400" />
                          )}
                        </button>
                        
                        {isExpanded && (
                          <div className="p-3 space-y-2 border-t border-slate-700">
                            {docs.map(doc => (
                              <button
                                key={doc.id}
                                onClick={() => handleSelectDocument(doc.id)}
                                className={`w-full text-left px-4 py-3 rounded-lg transition-colors ${
                                  selectedDocument === doc.id
                                    ? `bg-${color}-500/20 border border-${color}-500/50`
                                    : 'bg-slate-800/50 hover:bg-slate-800 border border-transparent'
                                }`}
                              >
                                <div className="flex items-center justify-between">
                                  <div>
                                    <p className="font-medium text-white">{doc.name}</p>
                                    <p className="text-xs text-slate-400 mt-1">{doc.description || doc.legal_basis}</p>
                                  </div>
                                  <ChevronRight className="w-4 h-4 text-slate-500" />
                                </div>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            
            {/* Features Sidebar */}
            <div className="space-y-4">
              <div className="bg-gradient-to-br from-purple-900/30 to-blue-900/30 border border-purple-500/30 rounded-xl p-5">
                <h4 className="font-semibold text-white mb-4 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-purple-400" />
                  Nuevas Funciones v2
                </h4>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-white">Validación de RUT</p>
                      <p className="text-xs text-slate-400">Verifica dígito verificador en tiempo real</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Calculator className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-white">Calculadora Integrada</p>
                      <p className="text-xs text-slate-400">Indemnizaciones y pensiones</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <FileDown className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-white">Exportar PDF</p>
                      <p className="text-xs text-slate-400">Formato profesional legal</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <DollarSign className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-white">Formato Montos</p>
                      <p className="text-xs text-slate-400">$1.000.000 automático</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Popular Documents */}
              {popularDocuments.length > 0 && (
                <div className="bg-card border border-border rounded-xl p-5">
                  <h4 className="font-semibold text-white mb-4 flex items-center gap-2">
                    <Star className="w-5 h-5 text-amber-400" />
                    Más Usados
                  </h4>
                  <div className="space-y-2">
                    {popularDocuments.slice(0, 5).map(doc => (
                      <button
                        key={doc.id}
                        onClick={() => handleSelectDocument(doc.id)}
                        className="w-full text-left px-3 py-2 rounded-lg bg-slate-800/50 hover:bg-slate-800 transition-colors"
                      >
                        <p className="text-sm font-medium text-white">{doc.name}</p>
                        <p className="text-xs text-slate-500">{doc.usage_count || 0} usos</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Generate Tab */}
        {activeTab === 'generate' && templateInfo && (
          <div className="grid md:grid-cols-3 gap-6">
            {/* Form */}
            <div className="md:col-span-2 space-y-4">
              <div className="bg-card border border-border rounded-xl p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-white flex items-center gap-2">
                    <FileText className="w-5 h-5 text-purple-400" />
                    {templateInfo.name}
                  </h3>
                  <span className="text-xs text-slate-400">
                    {templateInfo.required_fields?.length || 0} campos
                  </span>
                </div>
                
                {templateInfo.legal_basis && (
                  <div className="mb-4 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                    <p className="text-sm text-blue-400 flex items-center gap-2">
                      <Info className="w-4 h-4" />
                      Base legal: {templateInfo.legal_basis}
                    </p>
                  </div>
                )}
                
                <div className="grid grid-cols-2 gap-4">
                  {templateInfo.required_fields?.map((field, index) => renderInputField(field, index))}
                </div>
                
                <div className="flex gap-3 mt-6">
                  <button
                    onClick={handleGenerate}
                    disabled={generating}
                    className="flex-1 py-3 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                  >
                    {generating ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Sparkles className="w-5 h-5" />
                    )}
                    Generar Documento
                  </button>
                  
                  <button
                    onClick={handleGeneratePdf}
                    disabled={generatingPdf || !formData || Object.keys(formData).length === 0}
                    className="px-4 py-3 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 rounded-lg font-medium transition-colors flex items-center gap-2"
                    title="Descargar como PDF"
                  >
                    {generatingPdf ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <FileDown className="w-5 h-5" />
                    )}
                    PDF
                  </button>
                </div>
              </div>
              
              {/* Generated Document */}
              {generatedDocument && (
                <div className="bg-card border border-border rounded-xl p-5">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-white flex items-center gap-2">
                      <Check className="w-5 h-5 text-green-400" />
                      Documento Generado
                    </h3>
                    <div className="flex gap-2">
                      <button
                        onClick={handleCopy}
                        className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm flex items-center gap-2 transition-colors"
                      >
                        {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                        {copied ? 'Copiado' : 'Copiar'}
                      </button>
                    </div>
                  </div>
                  
                  <div className="bg-slate-900 rounded-lg p-4 max-h-[500px] overflow-y-auto">
                    <pre className="text-sm text-slate-300 whitespace-pre-wrap font-mono">
                      {generatedDocument.content}
                    </pre>
                  </div>
                  
                  <div className="mt-4 flex items-center gap-4 text-xs text-slate-500">
                    <span>Tribunal: {generatedDocument.court}</span>
                    <span>Generado: {new Date(generatedDocument.generated_at).toLocaleString('es-CL')}</span>
                  </div>
                </div>
              )}
            </div>
            
            {/* Calculator Sidebar */}
            <div className="space-y-4">
              {showCalculator === 'severance' && (
                <SeveranceCalculator 
                  onCalculated={handleCalculationComplete}
                  initialData={formData}
                />
              )}
              
              {showCalculator === 'alimony' && (
                <AlimonyCalculator onCalculated={handleCalculationComplete} />
              )}
              
              {/* Document Info */}
              <div className="bg-card border border-border rounded-xl p-5">
                <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
                  <Info className="w-5 h-5 text-blue-400" />
                  Información
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Tribunal:</span>
                    <span className="text-white">{templateInfo.court}</span>
                  </div>
                  {templateInfo.deadline && (
                    <div className="flex justify-between">
                      <span className="text-slate-400">Plazo:</span>
                      <span className="text-amber-400">{templateInfo.deadline}</span>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Tips */}
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-5">
                <h4 className="font-semibold text-amber-400 mb-3 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" />
                  Consejos
                </h4>
                <ul className="space-y-2 text-sm text-slate-300">
                  <li className="flex items-start gap-2">
                    <span className="text-amber-400">•</span>
                    Verifique todos los RUT antes de presentar
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-400">•</span>
                    Use la calculadora para montos precisos
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-400">•</span>
                    Revise el documento con un abogado
                  </li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Company Type Wizard Modal */}
      {showCompanyWizard && (
        <CompanyTypeWizard
          onSelect={(companyId) => handleSelectDocument(companyId)}
          onClose={() => setShowCompanyWizard(false)}
        />
      )}
    </div>
  );
};

export default LegalDocumentsPageEnhanced;
